
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import type { Visit, TimeRecord } from '@/types';

export const useVisitsData = () => {
  const { profile } = useAuth();
  const [todaysVisits, setTodaysVisits] = useState<Visit[]>([]);
  const [activeTimeRecord, setActiveTimeRecord] = useState<TimeRecord | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch today's visits
  const fetchTodaysVisits = async () => {
    if (!profile) return;

    try {
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('visits')
        .select(`
          *,
          patient:patients(id, first_name, last_name, address, date_of_birth, is_active, created_at, updated_at),
          time_records(*)
        `)
        .eq('nurse_id', profile.id)
        .eq('scheduled_date', today)
        .order('scheduled_start_time');

      if (error) throw error;
      
      // Transform the data to match our Visit type
      const transformedVisits: Visit[] = (data || []).map(visit => ({
        ...visit,
        patient: visit.patient ? {
          id: visit.patient.id,
          first_name: visit.patient.first_name,
          last_name: visit.patient.last_name,
          address: visit.patient.address,
          date_of_birth: visit.patient.date_of_birth,
          is_active: visit.patient.is_active,
          created_at: visit.patient.created_at,
          updated_at: visit.patient.updated_at
        } : undefined
      }));
      
      setTodaysVisits(transformedVisits);
      
      // Check for active time record
      const activeRecord = data?.find(visit => 
        visit.time_records?.some((record: any) => !record.clock_out_time)
      )?.time_records?.find((record: any) => !record.clock_out_time);
      
      if (activeRecord) {
        // Transform time record data with proper type checking
        const transformedRecord: TimeRecord = {
          ...activeRecord,
          clock_in_location: activeRecord.clock_in_location && 
            typeof activeRecord.clock_in_location === 'object' && 
            activeRecord.clock_in_location !== null &&
            'lat' in activeRecord.clock_in_location &&
            'lng' in activeRecord.clock_in_location
            ? activeRecord.clock_in_location as { lat: number; lng: number; address?: string }
            : undefined,
          clock_out_location: activeRecord.clock_out_location && 
            typeof activeRecord.clock_out_location === 'object' && 
            activeRecord.clock_out_location !== null &&
            'lat' in activeRecord.clock_out_location &&
            'lng' in activeRecord.clock_out_location
            ? activeRecord.clock_out_location as { lat: number; lng: number; address?: string }
            : undefined
        };
        setActiveTimeRecord(transformedRecord);
      }
    } catch (error) {
      console.error('Error fetching visits:', error);
      toast.error('Failed to load visits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodaysVisits();
  }, [profile]);

  return {
    todaysVisits,
    activeTimeRecord,
    loading,
    fetchTodaysVisits,
    setActiveTimeRecord
  };
};
